// IELTS Test Results page component

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ieltsAPI } from '../services/api';
import { IELTSTestResult, IELTSBandScoreInfo } from '../types';

const IELTSResults: React.FC = () => {
  const { resultId } = useParams<{ resultId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [result, setResult] = useState<IELTSTestResult | null>(null);
  const [bandScores, setBandScores] = useState<IELTSBandScoreInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (resultId) {
      loadTestResult(parseInt(resultId));
      loadBandScores();
    }
  }, [resultId]);

  const loadTestResult = async (id: number) => {
    try {
      const testResult = await ieltsAPI.getTestResult(id);
      setResult(testResult);
    } catch (err: any) {
      setError('Failed to load test results. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadBandScores = async () => {
    try {
      const scores = await ieltsAPI.getBandScores();
      setBandScores(scores);
    } catch (err: any) {
      console.error('Failed to load band scores:', err);
    }
  };

  const getBandColor = (band: string) => {
    const bandNum = parseFloat(band);
    if (bandNum >= 8.0) return 'bg-green-100 text-green-800';
    if (bandNum >= 7.0) return 'bg-blue-100 text-blue-800';
    if (bandNum >= 6.0) return 'bg-yellow-100 text-yellow-800';
    if (bandNum >= 5.0) return 'bg-orange-100 text-orange-800';
    return 'bg-red-100 text-red-800';
  };

  const getBandDescription = (band: string) => {
    const bandNum = parseFloat(band);
    if (bandNum >= 9.0) return 'Expert user - Has fully operational command of the language';
    if (bandNum >= 8.0) return 'Very good user - Has fully operational command with only occasional unsystematic inaccuracies';
    if (bandNum >= 7.0) return 'Good user - Has operational command of the language, though with occasional inaccuracies';
    if (bandNum >= 6.0) return 'Competent user - Has generally effective command despite some inaccuracies';
    if (bandNum >= 5.0) return 'Modest user - Has partial command of the language';
    if (bandNum >= 4.0) return 'Limited user - Basic competence is limited to familiar situations';
    if (bandNum >= 3.0) return 'Extremely limited user - Conveys and understands only general meaning';
    return 'Non-user - Essentially no ability to use the language';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto text-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
        <button
          onClick={() => navigate('/')}
          className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors"
        >
          Go Home
        </button>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="max-w-2xl mx-auto text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Test Result Not Found</h1>
        <p className="text-gray-600 mb-6">
          The requested test result could not be found.
        </p>
        <button
          onClick={() => navigate('/')}
          className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors"
        >
          Go Home
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">IELTS Test Results</h1>
        <p className="text-gray-600">
          Completed on {new Date(result.completed_at).toLocaleDateString()}
        </p>
      </div>

      {/* Overall Band Score */}
      <div className="bg-white rounded-lg shadow-md p-8 mb-8 text-center">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Overall Band Score</h2>
        <div className="text-6xl font-bold text-primary-600 mb-4">
          {result.overall_band || 'N/A'}
        </div>
        <p className="text-gray-600 text-lg">
          {getBandDescription(result.overall_band || '0.0')}
        </p>
      </div>

      {/* Section Scores */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Listening */}
        <div className="bg-white rounded-lg shadow-md p-6 text-center">
          <div className="text-2xl mb-2">🎧</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Listening</h3>
          <div className={`inline-block px-4 py-2 rounded-full text-2xl font-bold mb-2 ${getBandColor(result.listening_band || '0.0')}`}>
            {result.listening_band || 'N/A'}
          </div>
          <p className="text-sm text-gray-600">
            {result.listening_score || 0} correct answers
          </p>
        </div>

        {/* Reading */}
        <div className="bg-white rounded-lg shadow-md p-6 text-center">
          <div className="text-2xl mb-2">📖</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Reading</h3>
          <div className={`inline-block px-4 py-2 rounded-full text-2xl font-bold mb-2 ${getBandColor(result.reading_band || '0.0')}`}>
            {result.reading_band || 'N/A'}
          </div>
          <p className="text-sm text-gray-600">
            {result.reading_score || 0} correct answers
          </p>
        </div>

        {/* Writing */}
        <div className="bg-white rounded-lg shadow-md p-6 text-center">
          <div className="text-2xl mb-2">✍️</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Writing</h3>
          <div className={`inline-block px-4 py-2 rounded-full text-2xl font-bold mb-2 ${getBandColor(result.writing_band || '0.0')}`}>
            {result.writing_band || 'N/A'}
          </div>
          <p className="text-sm text-gray-600">
            {result.writing_score || 0} points earned
          </p>
        </div>

        {/* Speaking */}
        <div className="bg-white rounded-lg shadow-md p-6 text-center">
          <div className="text-2xl mb-2">🎤</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Speaking</h3>
          <div className={`inline-block px-4 py-2 rounded-full text-2xl font-bold mb-2 ${getBandColor(result.speaking_band || '0.0')}`}>
            {result.speaking_band || 'N/A'}
          </div>
          <p className="text-sm text-gray-600">
            {result.speaking_score || 0} points earned
          </p>
        </div>
      </div>

      {/* Band Score Information */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">IELTS Band Score Information</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {bandScores.slice(0, 9).map((band) => (
            <div key={band.band} className="p-3 border rounded-lg">
              <div className="flex justify-between items-center mb-1">
                <span className="font-semibold text-gray-900">Band {band.band}</span>
                <span className="text-sm text-gray-500">{band.percentage_range}</span>
              </div>
              <p className="text-sm text-gray-600">{band.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-center space-x-4 mb-8">
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
        >
          {showDetails ? 'Hide Details' : 'Show Detailed Results'}
        </button>
        <button
          onClick={() => navigate('/ielts')}
          className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          Take Another Test
        </button>
        <button
          onClick={() => navigate('/profile')}
          className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
        >
          View Profile
        </button>
      </div>

      {/* Detailed Results */}
      {showDetails && result.section_results && (
        <div className="space-y-6">
          {result.section_results.map((sectionResult) => (
            <div key={sectionResult.id} className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                {sectionResult.section_type.charAt(0).toUpperCase() + sectionResult.section_type.slice(1)} Section
              </h3>
              
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary-600">{sectionResult.band_score}</div>
                  <div className="text-sm text-gray-600">Band Score</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">{sectionResult.raw_score}</div>
                  <div className="text-sm text-gray-600">Raw Score</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">{sectionResult.time_taken_minutes}m</div>
                  <div className="text-sm text-gray-600">Time Taken</div>
                </div>
              </div>

              {/* Question-by-Question Results */}
              {sectionResult.user_answers && sectionResult.user_answers.length > 0 && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Question Details</h4>
                  <div className="space-y-4">
                    {sectionResult.user_answers.map((answer, index) => (
                      <div
                        key={answer.question_id}
                        className={`p-4 border rounded-lg ${
                          answer.is_correct
                            ? 'border-green-200 bg-green-50'
                            : 'border-red-200 bg-red-50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-900">
                            Question {answer.question_number}
                          </span>
                          <span
                            className={`px-2 py-1 rounded text-sm font-medium ${
                              answer.is_correct
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {answer.is_correct ? 'Correct' : 'Incorrect'}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600">
                          <div className="mb-1">
                            <span className="font-medium">Your answer:</span> {answer.user_answer}
                          </div>
                          {!answer.is_correct && (
                            <div className="mb-1">
                              <span className="font-medium">Correct answer:</span> {answer.correct_answer}
                            </div>
                          )}
                          {answer.explanation && (
                            <div>
                              <span className="font-medium">Explanation:</span> {answer.explanation}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default IELTSResults;

